// Local Storage Utilities

import { STORAGE_KEYS } from '../constants/app.constants';

export const StorageUtil = {
  // Token Management
  setAccessToken: (token) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  },

  getAccessToken: () => {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  setRefreshToken: (token) => {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  },

  getRefreshToken: () => {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  // User Info Management
  setUserInfo: (userInfo) => {
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
  },

  getUserInfo: () => {
    const userInfo = localStorage.getItem(STORAGE_KEYS.USER_INFO);
    return userInfo ? JSON.parse(userInfo) : null;
  },

  // Clear All Auth Data
  clearAuth: () => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  },

  // JWT expiry time in milliseconds. Returns null for opaque/non-JWT tokens.
  getTokenExpiry: (token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)) => {
    if (!token || typeof token !== 'string') return null;

    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) return null;

      const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      const payload = JSON.parse(window.atob(padded));
      const expiresAt = Number(payload.exp);

      return Number.isFinite(expiresAt) ? expiresAt * 1000 : null;
    } catch (_) {
      return null;
    }
  },

  isTokenExpired: (token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)) => {
    const expiresAt = StorageUtil.getTokenExpiry(token);
    return expiresAt !== null && expiresAt <= Date.now();
  },

  // Check if User is Authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    return !!token && !StorageUtil.isTokenExpired(token);
  },

  // Expenses Auth
  getExpensesAccessToken: () => localStorage.getItem(STORAGE_KEYS.EXPENSES_ACCESS_TOKEN),
  setExpensesAccessToken: (token) => localStorage.setItem(STORAGE_KEYS.EXPENSES_ACCESS_TOKEN, token),
  getExpensesRefreshToken: () => localStorage.getItem(STORAGE_KEYS.EXPENSES_REFRESH_TOKEN),
  setExpensesRefreshToken: (token) => localStorage.setItem(STORAGE_KEYS.EXPENSES_REFRESH_TOKEN, token),
  clearExpensesAuth: () => {
    localStorage.removeItem(STORAGE_KEYS.EXPENSES_ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.EXPENSES_REFRESH_TOKEN);
  },
  isExpensesAuthenticated: () => !!localStorage.getItem(STORAGE_KEYS.EXPENSES_ACCESS_TOKEN),

  // Cars team auth — their own login, in its own keys.
  getCarsAccessToken: () => localStorage.getItem(STORAGE_KEYS.CARS_ACCESS_TOKEN),
  setCarsAccessToken: (token) => localStorage.setItem(STORAGE_KEYS.CARS_ACCESS_TOKEN, token),
  getCarsRefreshToken: () => localStorage.getItem(STORAGE_KEYS.CARS_REFRESH_TOKEN),
  setCarsRefreshToken: (token) => localStorage.setItem(STORAGE_KEYS.CARS_REFRESH_TOKEN, token),
  setCarsStaff: (staff) => localStorage.setItem(STORAGE_KEYS.CARS_STAFF, JSON.stringify(staff || {})),
  getCarsStaff: () => {
    const raw = localStorage.getItem(STORAGE_KEYS.CARS_STAFF);
    try {
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;   // a half-written value must not break the whole app
    }
  },
  clearCarsAuth: () => {
    localStorage.removeItem(STORAGE_KEYS.CARS_ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.CARS_REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.CARS_STAFF);
  },
  isCarsAuthenticated: () => !!localStorage.getItem(STORAGE_KEYS.CARS_ACCESS_TOKEN),
};

export default StorageUtil;
